import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWhatsappStatus, setWhatsappLinkMode, reconnectWhatsapp } from '../api';
import WhatsAppChats from './WhatsAppChats';

/**
 * WhatsApp linking panel. Polls the server for the connection state and shows
 * whichever linking method is active: a scannable QR, or an 8-character pairing
 * code entered on the phone. Linking persists in the bot's session volume, so
 * it's normally a one-time step.
 */
export default function WhatsAppConnect({ onAuthError }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  // Local form state. Kept separate from `info` so the 4s status poll can't
  // clobber a half-typed phone number.
  const [mode, setMode] = useState('qr');
  const [phone, setPhone] = useState('');
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const seeded = useRef(false);

  const load = useCallback(async () => {
    try {
      const s = await fetchWhatsappStatus();
      setInfo(s);
      setError('');
      // Adopt the server's method once, on first load — after that the radio
      // reflects what the admin is choosing, not what the poll returns.
      if (!seeded.current) {
        seeded.current = true;
        if (s.linkMode) setMode(s.linkMode);
        if (s.phoneNumber) setPhone(s.phoneNumber);
      }
    } catch (err) {
      if (err.message === 'AUTH') {
        onAuthError && onAuthError();
        return;
      }
      setError('Could not load WhatsApp status.');
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  const applyMode = useCallback(
    async (nextMode, nextPhone) => {
      setSwitching(true);
      setSwitchError('');
      try {
        const res = await setWhatsappLinkMode(nextMode, nextPhone);
        setInfo((prev) => ({ ...prev, ...res }));
      } catch (err) {
        if (err.message === 'AUTH') {
          onAuthError && onAuthError();
          return;
        }
        setSwitchError(err.message);
      } finally {
        setSwitching(false);
      }
    },
    [onAuthError]
  );

  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState('');

  const forceReconnect = useCallback(async () => {
    setReconnecting(true);
    setReconnectError('');
    try {
      const res = await reconnectWhatsapp();
      setInfo((prev) => ({ ...prev, ...res }));
    } catch (err) {
      if (err.message === 'AUTH') {
        onAuthError && onAuthError();
        return;
      }
      setReconnectError(err.message);
    } finally {
      setReconnecting(false);
    }
  }, [onAuthError]);

  const chooseMode = (next) => {
    setMode(next);
    setSwitchError('');
    // Switching back to QR needs no extra input, so apply it immediately.
    // Phone mode waits for a number to be entered and submitted.
    if (next === 'qr' && info?.linkMode === 'phone') applyMode('qr');
  };

  if (!info) {
    return <p className="hint">Loading WhatsApp status…</p>;
  }

  if (!info.enabled) {
    return (
      <p className="hint">
        WhatsApp is not enabled. Set <code>ENABLE_WHATSAPP=true</code> and{' '}
        <code>WHATSAPP_GROUP_ID</code> in the environment, then redeploy — the
        linking options will appear here.
      </p>
    );
  }

  // Only 'ready' can actually send. 'authenticated' means the session was
  // accepted but the client never finished coming up — the panel used to show
  // that as connected, which contradicted broadcasts failing with
  // "not ready (authenticated)". Show it for what it is, with a way out.
  const connected = info.ready || info.state === 'ready';
  const halfConnected = !connected && info.state === 'authenticated';

  if (connected || halfConnected) {
    return (
      <div>
        {connected ? (
          <p className="wa-status wa-ok">✓ WhatsApp is connected.</p>
        ) : (
          <p className="wa-status wa-warn">
            ⚠ WhatsApp authenticated but isn't ready to send yet. It should
            recover on its own within 10 minutes — use Reconnect to force it now.
          </p>
        )}
        <div className="wa-actions">
          <button
            type="button"
            className="btn secondary"
            onClick={forceReconnect}
            disabled={reconnecting}
          >
            {reconnecting ? 'Reconnecting…' : 'Reconnect'}
          </button>
        </div>
        {reconnectError && <p className="wa-status wa-error">{reconnectError}</p>}
        {connected && <WhatsAppChats onAuthError={onAuthError} />}
      </div>
    );
  }

  return (
    <div className="wa-link">
      <div className="wa-mode-picker">
        <label className="wa-mode-option">
          <input
            type="radio"
            name="wa-link-mode"
            checked={mode === 'qr'}
            disabled={switching}
            onChange={() => chooseMode('qr')}
          />
          <span>Scan QR code</span>
        </label>
        <label className="wa-mode-option">
          <input
            type="radio"
            name="wa-link-mode"
            checked={mode === 'phone'}
            disabled={switching}
            onChange={() => chooseMode('phone')}
          />
          <span>Link with phone number</span>
        </label>
      </div>

      {mode === 'phone' && (
        <form
          className="wa-phone-form"
          onSubmit={(e) => {
            e.preventDefault();
            applyMode('phone', phone);
          }}
        >
          <input
            type="text"
            value={phone}
            placeholder="38641234567"
            disabled={switching}
            onChange={(e) => setPhone(e.target.value)}
            aria-label="WhatsApp phone number in international format"
          />
          <button type="submit" className="btn primary" disabled={switching || !phone.trim()}>
            {switching ? 'Working…' : 'Get code'}
          </button>
        </form>
      )}

      {switchError && <p className="wa-status wa-error">{switchError}</p>}

      {mode === 'phone' && info.pairingCode && (
        <div className="wa-pairing">
          <p className="hint">
            On your phone: <strong>WhatsApp → Linked devices → Link a device →
            Link with phone number instead</strong>, then enter:
          </p>
          <div className="wa-pairing-code">{info.pairingCode}</div>
          <p className="hint">
            The code renews every few minutes until you enter it.
          </p>
        </div>
      )}

      {mode === 'qr' && info.qr && (
        <div className="wa-qr">
          <p className="hint">
            On your phone: <strong>WhatsApp → Settings → Linked Devices → Link a
            Device</strong>, then scan this code:
          </p>
          <img className="wa-qr-img" src={info.qr} alt="WhatsApp QR code" width={280} height={280} />
          <p className="hint">The code refreshes automatically until you scan it.</p>
        </div>
      )}

      {!info.qr && !info.pairingCode && (
        <p className="hint">
          {switching
            ? 'Reconnecting WhatsApp with the new linking method…'
            : mode === 'phone'
              ? `Enter the number this bot's WhatsApp account uses, then press Get code. (state: ${info.state})`
              : `Waiting for WhatsApp to produce a QR code… (state: ${info.state})`}
          {error && ` — ${error}`}
        </p>
      )}
    </div>
  );
}
